import {useMemo, type JSX} from 'react'
import {type GuidType} from '../../values/generic-type-definitions'
import {type VesselTag} from '../../api/vessels/vessel-tags'
import {Tree, TreeItem, type TreeItemProps} from '../base/tree/tree'
import {type Key, type Selection} from 'react-stately'
import {Collection} from 'react-aria-components'
import {FlexAlignments, FlexRow} from '../base/flexbox.styled'
import {type Tag, type Vessel} from './vessel-selector.types'
import Styled from './vessel-selector-tree.styled'
import {DropdownIcon} from '../media/icons/dropdown-icon/dropdown-icon'

type VesselNode = {
    id: string
    _vessel: Vessel
}
type VesselTagNode = {
    id: string
    _tag: Tag
    _vessels: VesselNode[]
}

type AnyNode = VesselNode | VesselTagNode

const WHOLE_FLEET_LABEL = 'Whole Fleet'

export interface VesselSelectorTreeProps extends React.RefAttributes<HTMLDivElement> {
    'aria-label'?: string
    filter?: string
    selectionMode: 'single' | 'multiple'
    vesselsWithTags: Vessel[]
    selectedVesselIds: Set<GuidType>
    isDefaultExpanded?: boolean
    isDisabled?: boolean
    granularMode?: boolean
    onChange: (selectedVesselIds: Set<GuidType>) => void
    className?: string
}

export function VesselSelectorTree(props: VesselSelectorTreeProps): JSX.Element {
    const items = useMemo(() => createTree(props.vesselsWithTags), [props.vesselsWithTags])

    const expandibleKeys = useMemo(
        () => (props.isDefaultExpanded ? getAllExpandibleKeys(items) : undefined),
        [props.isDefaultExpanded, items],
    )

    const selectedNodeIds = useMemo(
        () => getSelectedNodeIdsWithTags(props.selectedVesselIds, items),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.selectedVesselIds, items],
    )

    const hasItems = items.length > 0

    return (
        <Tree
            aria-label={props['aria-label'] ?? 'Vessel Selector Tree'}
            items={items}
            selectedKeys={selectedNodeIds}
            selectionMode={props.selectionMode}
            onSelectionChange={onSelectionChange}
            renderEmptyState={EmptyState}
            defaultExpandedKeys={expandibleKeys}
            filter={props.filter}
            className={props.className}
            isDisabled={props.isDisabled}
            granularMode={props.granularMode}
            renderExpandIcon={(isExpanded) => (
                <DropdownIcon isSelected={isExpanded} rotateFromSide={true} />
            )}
        >
            {hasItems && (
                <TreeItem
                    id="all"
                    textValue={WHOLE_FLEET_LABEL}
                    value={props.vesselsWithTags}
                    renderItem={WholeFleetItem}
                >
                    <Collection items={items}>
                        {function renderItem(item: AnyNode) {
                            if (isVesselTagNode(item)) {
                                return (
                                    <TreeItem
                                        value={item}
                                        textValue={item._tag.name}
                                        renderItem={TagItem}
                                    >
                                        <Collection items={item._vessels}>{renderItem}</Collection>
                                    </TreeItem>
                                )
                            } else {
                                return (
                                    <TreeItem
                                        value={item}
                                        textValue={item._vessel.description}
                                        renderItem={VesselItem}
                                    />
                                )
                            }
                        }}
                    </Collection>
                </TreeItem>
            )}
        </Tree>
    )

    function onSelectionChange(selectedKeys: Selection) {
        const selectedVesselIds = getSelectedVesselIds(selectedKeys)
        props.onChange(selectedVesselIds)
    }

    function getSelectedVesselIds(selectedKeys: Selection): Set<GuidType> {
        if (selectedKeys === 'all') {
            return new Set(props.vesselsWithTags.map((v) => v.location))
        }

        const prevSelection = selectedNodeIds
        const newSelection = new Set(props.selectedVesselIds)

        const addedKeys = [...selectedKeys].filter((k) => !prevSelection.has(k))
        if (addedKeys.includes('all')) {
            return new Set(props.vesselsWithTags.map((v) => v.location))
        }
        processAddedKeys(addedKeys, newSelection)

        const removedKeys = [...prevSelection].filter((k) => !selectedKeys.has(k))
        if (removedKeys.includes('all')) {
            return new Set()
        }
        processRemovedKeys(removedKeys, newSelection)

        return newSelection
    }

    function processAddedKeys(addedKeys: Key[], selectedVesselIds: Set<GuidType>) {
        for (const key of addedKeys) {
            const node = findNode(key, items)
            if (!node) {
                continue
            }

            if (isVesselTagNode(node)) {
                for (const vessel of node._vessels) {
                    selectedVesselIds.add(vessel._vessel.location)
                }
            } else {
                selectedVesselIds.add(node._vessel.location)
            }
        }
    }

    function processRemovedKeys(removedKeys: Key[], selectedVesselIds: Set<GuidType>) {
        for (const key of removedKeys) {
            const node = findNode(key, items)
            if (!node) {
                continue
            }

            if (isVesselTagNode(node)) {
                for (const vessel of node._vessels) {
                    selectedVesselIds.delete(vessel._vessel.location)
                }
            } else {
                selectedVesselIds.delete(node._vessel.location)
            }
        }
    }

    function getSelectedNodeIdsWithTags(
        selectedVesselIds: Set<GuidType>,
        items: AnyNode[],
    ): Set<Key> {
        const selectedNodeIds = collectSelectedVesselNodeIds(selectedVesselIds, items)
        addFullySelectedTagIds(selectedNodeIds, items)
        updateAllNodeSelection(selectedNodeIds, selectedVesselIds)

        return selectedNodeIds
    }

    function collectSelectedVesselNodeIds(
        selectedVesselIds: Set<GuidType>,
        items: AnyNode[],
    ): Set<Key> {
        return new Set<Key>(
            items.flatMap((item) => {
                if (isVesselTagNode(item)) {
                    return item._vessels
                        .filter((vessel) => selectedVesselIds.has(vessel._vessel.location))
                        .map((vessel) => vessel.id)
                } else {
                    return selectedVesselIds.has(item._vessel.location) ? [item.id] : []
                }
            }),
        )
    }

    function addFullySelectedTagIds(selectedNodeIds: Set<Key>, items: AnyNode[]): void {
        const fullySelectedTags = items.filter(
            (item) => isVesselTagNode(item) && areAllVesselsInTagSelected(item, selectedNodeIds),
        ) as VesselTagNode[]

        for (const tag of fullySelectedTags) {
            selectedNodeIds.add(tag.id)
        }
    }

    function updateAllNodeSelection(
        selectedNodeIds: Set<Key>,
        selectedVesselIds: Set<GuidType>,
    ): void {
        const allVessels = props.vesselsWithTags.map((v) => v.location)
        if (allVessels.every((v) => selectedVesselIds.has(v))) {
            selectedNodeIds.add('all')
        } else {
            selectedNodeIds.delete('all')
        }
    }

    function areAllVesselsInTagSelected(
        tagNode: VesselTagNode,
        selectedNodeIds: Set<Key>,
    ): boolean {
        return tagNode._vessels.every((vessel) => selectedNodeIds.has(vessel.id))
    }
}

function WholeFleetItem({value}: TreeItemProps<Vessel[]>): JSX.Element {
    const count = value?.length ?? 0
    return (
        <>
            <Styled.ElipsisSpan>{WHOLE_FLEET_LABEL}</Styled.ElipsisSpan>
            <Styled.Count>
                {count} {count === 1 ? 'vessel' : 'vessels'}
            </Styled.Count>
        </>
    )
}

function TagItem({value}: TreeItemProps<VesselTagNode>): JSX.Element {
    const count = value?._vessels.length ?? 0
    return (
        <>
            <Styled.VesselTagResult onClickFn={() => {}} tagName={value?._tag.name ?? ''} />
            <Styled.Count>
                {count} {count === 1 ? 'vessel' : 'vessels'}
            </Styled.Count>
        </>
    )
}

function VesselItem({value}: TreeItemProps<VesselNode>): JSX.Element {
    return <Styled.ElipsisSpan>{value?._vessel.description}</Styled.ElipsisSpan>
}

function EmptyState(): JSX.Element {
    return <FlexRow justifyContent={FlexAlignments.Center}>No vessels found</FlexRow>
}

function findNode(key: Key, items: AnyNode[]): AnyNode | null {
    for (const item of items) {
        if (item.id === key) {
            return item
        }
        if (isVesselTagNode(item)) {
            for (const v of item._vessels) {
                if (v.id === key) {
                    return v
                }
            }
        }
    }
    return null
}

function isVesselTagNode(item: AnyNode): item is VesselTagNode {
    return '_vessels' in item
}

function createTree(vesselsWithTags: Vessel[]): AnyNode[] {
    const tags = new Map<
        GuidType,
        {
            vessels: Vessel[]
            tag: Tag
        }
    >()
    const vesselsWithoutTags: Vessel[] = []

    if (vesselsWithTags.length === 0) {
        return []
    }

    for (const vessel of vesselsWithTags) {
        if (vessel.tags && vessel.tags.length > 0) {
            for (const tag of vessel.tags) {
                const existingTag = tags.get(tag.identity)
                if (existingTag) {
                    existingTag.vessels.push(vessel)
                } else {
                    tags.set(tag.identity, {tag, vessels: [vessel]})
                }
            }
        } else {
            vesselsWithoutTags.push(vessel)
        }
    }

    const tagNodes: VesselTagNode[] = Array.from(tags.values())
        .map(({tag, vessels}) => ({
            _tag: tag,
            id: getVesselTagNodeId(tag),
            _vessels: vessels
                .map((vessel) => ({
                    _vessel: vessel,
                    id: `${getVesselTagNodeId(tag)}/${getVesselNodeId(vessel)}`,
                }))
                .sort((a, b) => a._vessel.description.localeCompare(b._vessel.description)),
        }))
        .sort((a, b) => a._tag.name.localeCompare(b._tag.name))

    const vesselNodes: VesselNode[] = vesselsWithoutTags
        .map((vessel) => ({
            _vessel: vessel,
            id: getVesselNodeId(vessel),
        }))
        .sort((a, b) => a._vessel.description.localeCompare(b._vessel.description))

    return [...tagNodes, ...vesselNodes]
}

function getVesselTagNodeId(tag: VesselTag): string {
    return 'T' + tag.identity
}

function getVesselNodeIdFromVesselId(vesselId: GuidType): string {
    return 'V' + vesselId
}

function getVesselNodeId(vessel: Vessel): string {
    return getVesselNodeIdFromVesselId(vessel.location)
}

function getAllExpandibleKeys(items: AnyNode[]): Set<Key> {
    const keys = new Set(items.filter(isVesselTagNode).map((item) => item.id))
    keys.add('all')
    return keys
}
